class SearchModal extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.search-modal__input');
    this.form = this.querySelector('form');
    this.defaultPanel = this.querySelector('[data-search-default]');
    this.results = this.querySelector('[data-search-results]');
    this.loader = this.querySelector('[data-search-loader]');
    this.popular = this.querySelector('[data-search-popular]');
    this.body = this.querySelector('[data-search-body]');
    this.recent = this.querySelector('[data-recent]');
    this.recentList = this.querySelector('[data-recent-list]');
    this.storageKey = 'bz-recent-searches';
    this.abortController = null;
    this.opener = null;
    this.lockedScroll = false;

    this.onInput = this.debounce(() => this.search(), 300);
    this.input?.addEventListener('input', this.onInput);
    this.form?.addEventListener('submit', () => {
      this.remember(this.input?.value || '');
    });

    this.addEventListener('click', (event) => {
      if (this.isBackdrop(event)) {
        this.close();
        return;
      }
      const termLink = event.target.closest('.js-search-term');
      if (termLink) this.remember(termLink.dataset.term || termLink.textContent);
    });

    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
    };
    this.onDocumentClick = (event) => {
      const opener = event.target.closest('[data-search-open]');
      if (opener) {
        event.preventDefault();
        this.opener = opener;
        this.open();
        return;
      }
      if (!this.classList.contains('is-open')) return;
      if (this.isBackdrop(event) || !this.contains(event.target)) this.close();
    };
    this.onResize = () => {
      if (this.classList.contains('is-open')) this.setTop();
    };
    this.onSectionLoad = () => this.syncLayout();
    this.onSectionSelect = (event) => {
      if (event.target === this || this.contains(event.target) || event.target.contains?.(this)) this.open();
    };
    this.onSectionDeselect = (event) => {
      if (event.target.contains?.(this)) this.close();
    };
  }

  connectedCallback() {
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.onResize);
    document.addEventListener('shopify:section:load', this.onSectionLoad);
    this.syncLayout();
    if (this.dataset.designMode === 'true') {
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:block:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
    }
    this.renderRecent();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    document.removeEventListener('shopify:block:select', this.onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
  }

  isBackdrop(event) {
    return event.target === this || event.target.hasAttribute('data-search-backdrop') || !!event.target.closest('[data-search-close]');
  }

  syncLayout() {
    const drawer = document.querySelector('.bz_mega_drawer');
    const grid = drawer?.classList.contains('mega_layout_grid');
    this.classList.toggle('mega_layout_grid', !!grid);
    this.classList.toggle('mega_layout_full_width', !grid);
  }

  setTop() {
    const drawer = document.querySelector('.bz_mega_drawer');
    if (drawer?.isMegaDrawer) drawer._setDrawerTop?.();
    const drawerTop = drawer && getComputedStyle(drawer).getPropertyValue('--drawer-top').trim();
    if (drawerTop) {
      this.style.setProperty('--search-top', drawerTop);
      return;
    }
    const header = document.querySelector('header[data-header]') || document.querySelector('.bz_mega_drawer')?.closest('header');
    const top = header ? Math.max(header.getBoundingClientRect().top, 0) : 0;
    this.style.setProperty('--search-top', `${top}px`);
  }

  open() {
    this.syncLayout();
    this.classList.add('is-open');
    this.removeAttribute('inert');
    this.setAttribute('aria-hidden', 'false');
    if (!document.body.classList.contains('o-hidden')) {
      document.body.classList.add('o-hidden');
      this.lockedScroll = true;
    }
    this.setTop();
    if (this.dataset.designMode !== 'true') {
      window.BzA11y?.trapFocus(this, { initial: false, returnTo: this.opener || document.activeElement });
    }
    window.setTimeout(() => {
      this.input?.focus({ preventScroll: true });
      this.querySelectorAll('bz-slider').forEach((slider) => slider.swiperInstance?.update());
    }, 40);
  }

  close() {
    window.BzA11y?.releaseFocus(this, { restore: false });
    this.classList.remove('is-open');
    this.setAttribute('inert', '');
    this.setAttribute('aria-hidden', 'true');
    if (this.lockedScroll) {
      document.body.classList.remove('o-hidden');
      this.lockedScroll = false;
    }
    this.abortController?.abort();
    this.showDefault();
    if (this.input) this.input.value = '';
    this.opener?.focus?.();
  }

  search() {
    const term = this.input?.value.trim() || '';
    if (!term.length) {
      this.abortController?.abort();
      this.showDefault();
      return;
    }

    const controller = new AbortController();
    this.abortController?.abort();
    this.abortController = controller;
    this.loader?.classList.remove('hidden');
    this.loader?.querySelector('.loader_state')?.classList.remove('hidden');

    const endpoint = this.dataset.predictiveUrl || '/search/suggest';
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product,collection,article,page');
    url.searchParams.set('resources[limit]', '8');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', 'search-term');

    fetch(url.toString(), { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((html) => {
        if (this.abortController !== controller) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector('#shopify-section-search-term') || doc.querySelector('.shopify-section');
        if (!this.results) return;
        if (section) this.matchDefaultLayout(section);
        // Show the results box before adding the sliders, so they measure the right width
        this.defaultPanel?.classList.add('hidden');
        this.results.classList.remove('hidden');
        this.results.innerHTML = section ? section.innerHTML : '';
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.showDefault();
      })
      .finally(() => {
        if (this.abortController !== controller) return;
        this.loader?.classList.add('hidden');
        this.loader?.querySelector('.loader_state')?.classList.add('hidden');
      });
  }

  // Live results use the same look as the default blocks: column limits, card color schemes,
  // heading size and image ratio are copied from the default Products / Collections blocks.
  // Runs on the parsed (not yet live) markup, so bz-slider starts with the copied settings.
  matchDefaultLayout(root) {
    const sliderAttrs = ['data-breakpoints', 'data-section-setting', 'data-spacing-grid-horizontal'];
    const isScheme = (name) => name.startsWith('scheme-');
    const setScheme = (el, scheme) => {
      if (!el || !scheme) return;
      [...el.classList].filter(isScheme).forEach((name) => el.classList.remove(name));
      el.classList.add(scheme);
    };
    const swapClass = (el, test, value) => {
      if (!el || !value) return;
      [...el.classList].filter(test).forEach((name) => el.classList.remove(name));
      el.classList.add(value);
    };

    ['products', 'collections'].forEach((type) => {
      const source = this.defaultPanel?.querySelector(`[data-search-type="${type}"]`);
      const target = root.querySelector(`[data-search-type="${type}"]`);
      if (!source || !target) return;

      const sourceSlider = source.querySelector('bz-slider');
      const targetSlider = target.querySelector('bz-slider');
      if (sourceSlider && targetSlider) {
        sliderAttrs.forEach((attr) => {
          if (sourceSlider.hasAttribute(attr)) targetSlider.setAttribute(attr, sourceSlider.getAttribute(attr));
        });
        // Carousel buttons: same as the default block ("Enable carousel buttons")
        const sourceArrows = sourceSlider.querySelector('.slider_arows');
        const targetSwiper = targetSlider.querySelector('.swiper');
        if (sourceArrows && targetSwiper && !targetSwiper.querySelector('.slider_arows')) {
          const arrows = sourceArrows.cloneNode(true);
          arrows.querySelectorAll('.swiper-button-disabled, .swiper-button-lock').forEach((btn) => {
            btn.classList.remove('swiper-button-disabled', 'swiper-button-lock');
          });
          arrows.querySelectorAll('[aria-disabled], [tabindex], [aria-controls]').forEach((btn) => {
            btn.removeAttribute('aria-disabled');
            btn.removeAttribute('tabindex');
            btn.removeAttribute('aria-controls');
          });
          targetSwiper.appendChild(root.ownerDocument.importNode(arrows, true));
        }
      }

      if (type === 'products') {
        // Product color metafield cards keep their own color (has-product-color)
        const scheme = source.dataset.cardScheme;
        target.querySelectorAll('.card.gradient').forEach((card) => setScheme(card, scheme));
        return;
      }

      // Collections: card color schemes repeat in the same order as the default columns
      const schemes = [...source.querySelectorAll('.bz-multicolumn__card')]
        .map((card) => [...card.classList].find(isScheme))
        .filter(Boolean);
      const sourceTitle = source.querySelector('.bz-heading__title');
      const titleSize = sourceTitle && [...sourceTitle.classList].find((name) => name.startsWith('f_'));
      const sourceImage = source.querySelector('.bz-block-image');
      const imageRatio = sourceImage && [...sourceImage.classList].find((name) => name.startsWith('bz-block-image--'));

      target.querySelectorAll('[data-search-card]').forEach((card, index) => {
        if (schemes.length) setScheme(card, schemes[index % schemes.length]);
        swapClass(card.querySelector('.bz-heading__title'), (name) => name.startsWith('f_'), titleSize);
        swapClass(card.querySelector('.bz-block-image'), (name) => name.startsWith('bz-block-image--'), imageRatio);
      });
    });
  }

  showDefault() {
    this.defaultPanel?.classList.remove('hidden');
    this.results?.classList.add('hidden');
    if (this.results) this.results.innerHTML = '';
    this.popular?.classList.remove('hidden');
    if (this.popular) this.body?.classList.remove('search-modal__body--full');
    this.loader?.classList.add('hidden');
  }

  readRecent() {
    try {
      const items = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
      return Array.isArray(items) ? items.filter((item) => typeof item === 'string' && item.trim()) : [];
    } catch (error) {
      return [];
    }
  }

  remember(term) {
    const value = (term || '').trim();
    if (!value) return;
    const items = this.readRecent().filter((item) => item.toLowerCase() !== value.toLowerCase());
    items.unshift(value);
    localStorage.setItem(this.storageKey, JSON.stringify(items.slice(0, 8)));
    this.renderRecent();
  }

  renderRecent() {
    if (!this.recent || !this.recentList) return;
    const items = this.readRecent();
    // Recent searches show only after the shopper has searched (no preview, also in the editor)
    if (!items.length) {
      this.recent.classList.add('hidden');
      this.recentList.innerHTML = '';
      return;
    }

    const base = this.dataset.searchUrl || '/search';
    this.recentList.innerHTML = items.map((term) => {
      const href = `${base}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
      return `<a class="js-search-term" href="${href}" data-term="${this.escapeHtml(term)}">${this.escapeHtml(term)}</a>`;
    }).join('');
    this.recent.classList.remove('hidden');
  }

  escapeHtml(value) {
    return value.replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[char]));
  }

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('bz-search-modal', SearchModal);